import type { ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import {
    alpha,
    Alert,
    Box,
    Button,
    CircularProgress,
    Card,
    CardContent,
    FormControl,
    FormControlLabel,
    InputLabel,
    LinearProgress,
    Link,
    MenuItem,
    Select,
    Switch,
    TableCell,
    TableSortLabel,
    Typography,
} from '@mui/material';
import { useCommonTranslation } from 'c-translation';
import { useDateUtils } from 'c-hooks';
import { USAGE_PERIODS } from './usageLib';

/** Building blocks shared by the four usage pages. */

/** Column headers as on the board: small muted capitals, so the numbers carry the table. */
export const tableHeadSx = {
    '& th': {
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        whiteSpace: 'nowrap',
    },
} as const;
export type TableSort<K extends string> = { key: K; direction: 'asc' | 'desc' };

/**
 * A column header that sorts its table. The first click sorts the way people read that column
 * (`first`: biggest or latest first for numbers and dates, A–Z for names); the next flips it.
 */
export const SortHeader = <K extends string>({
    column,
    sort,
    onSort,
    first = 'desc',
    align,
    children,
}: {
    column: K;
    sort: TableSort<K> | null;
    onSort: (sort: TableSort<K>) => void;
    first?: 'asc' | 'desc';
    align?: 'right';
    children: ReactNode;
}) => {
    const active = sort?.key === column;
    const direction = active ? sort.direction : first;
    return (
        <TableCell align={align} sortDirection={active ? direction : false}>
            <TableSortLabel
                active={active}
                direction={direction}
                onClick={() =>
                    onSort({
                        key: column,
                        direction: active ? (direction === 'asc' ? 'desc' : 'asc') : first,
                    })
                }
            >
                {children}
            </TableSortLabel>
        </TableCell>
    );
};

/** A titled card with an optional hint and something on the right (a key, a toggle). */
export const UsageSection = ({
    title,
    hint,
    aside,
    children,
}: {
    title: string;
    hint?: string;
    aside?: React.ReactNode;
    children: React.ReactNode;
}) => (
    <Card
        variant="outlined"
        component="section"
        aria-label={title}
        sx={{
            p: { xs: 2, md: 3 },
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            minWidth: 0,
        }}
    >
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                gap: 2,
                flexWrap: 'wrap',
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography component="h2" variant="subtitle1" fontWeight={600}>
                    {title}
                </Typography>
                {hint && (
                    <Typography variant="body2" color="text.secondary">
                        {hint}
                    </Typography>
                )}
            </Box>
            {aside}
        </Box>
        {children}
    </Card>
);

export const UsagePanel = ({ title, children }: { title?: ReactNode; children: ReactNode }) => (
    <Card variant="outlined" sx={{ minWidth: 0 }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {title != null && (
                <Typography component="h2" variant="subtitle1" fontWeight={600}>
                    {title}
                </Typography>
            )}
            {children}
        </CardContent>
    </Card>
);

export const KpiGrid = ({ children }: { children: ReactNode }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 2,
        }}
    >
        {children}
    </Box>
);

export const KpiTile = ({
    label,
    value,
    note,
    delta,
    href,
    linkLabel,
}: {
    label: string;
    value: ReactNode;
    note?: ReactNode;
    delta?: number | null;
    /** Makes the value a link, e.g. to the table behind the number. */
    href?: string;
    linkLabel?: string;
}) => {
    const t = useCommonTranslation();
    let deltaText: string | null = null;
    if (delta != null) deltaText = `${delta >= 0 ? '▲' : '▼'} ${Math.abs(delta)}%`;

    return (
        <Card variant="outlined">
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                    {label}
                </Typography>
                <Typography variant="h5" component="p" fontWeight={700}>
                    {href ? (
                        <Link href={href} underline="hover" aria-label={linkLabel}>
                            {value}
                        </Link>
                    ) : (
                        value
                    )}
                </Typography>
                {deltaText != null && (
                    <Typography
                        variant="body2"
                        color={delta != null && delta < 0 ? 'error.main' : 'success.main'}
                    >
                        {deltaText}{' '}
                        <Typography component="span" variant="body2" color="text.secondary">
                            {t('Modules.Admin.Usage.vsPrevious')}
                        </Typography>
                    </Typography>
                )}
                {note != null && (
                    <Typography variant="body2" color="text.secondary">
                        {note}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export const ShareBar = ({ percent, label }: { percent: number; label: string }) => (
    <LinearProgress
        variant="determinate"
        value={percent}
        aria-label={label}
        sx={{ height: 8, borderRadius: 999, minWidth: 80 }}
    />
);

/** Tiny column chart for a trend; decorative, the table carries the numbers. */
export const TrendBars = ({ points }: { points: number[] }) => {
    const max = Math.max(1, ...points);
    return (
        <Box
            aria-hidden="true"
            sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 24 }}
        >
            {points.map((point, index) => (
                <Box
                    // eslint-disable-next-line react/no-array-index-key -- fixed-length series
                    key={index}
                    sx={{
                        width: 6,
                        height: `${Math.max(8, (point / max) * 100)}%`,
                        bgcolor: point > 0 ? 'primary.light' : 'action.disabledBackground',
                        borderRadius: '1px',
                    }}
                />
            ))}
        </Box>
    );
};

export const PeriodSelect = ({
    value,
    onChange,
}: {
    value: number;
    onChange: (days: number) => void;
}) => {
    const t = useCommonTranslation();
    return (
        <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="usage-period-label">{t('Modules.Admin.Usage.period')}</InputLabel>
            <Select
                labelId="usage-period-label"
                label={t('Modules.Admin.Usage.period')}
                value={value}
                onChange={event => onChange(Number(event.target.value))}
            >
                {USAGE_PERIODS.map(days => (
                    <MenuItem key={days} value={days}>
                        {t('Modules.Admin.Usage.periodDays', { count: days })}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

/** Staff (admin permissions) are left out of the numbers unless this is on. */
export const StaffToggle = ({
    checked,
    onChange,
}: {
    checked: boolean;
    onChange: (include: boolean) => void;
}) => {
    const t = useCommonTranslation();
    return (
        // No Tooltip: MUI gives its child the tooltip text as aria-label, which would replace the
        // switch's name. The hint is shown on hover through `title` on the label instead.
        <FormControlLabel
            title={t('Modules.Admin.Usage.includeStaffHint')}
            control={
                <Switch checked={checked} onChange={event => onChange(event.target.checked)} />
            }
            label={t('Modules.Admin.Usage.includeStaff')}
        />
    );
};

export const UsageHeader = ({
    title,
    subtitle,
    controls,
}: {
    title: ReactNode;
    subtitle?: ReactNode;
    controls?: ReactNode;
}) => (
    <Box
        sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'flex-end',
            justifyContent: 'space-between',
        }}
    >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography component="h1" variant="h5" fontWeight={700}>
                {title}
            </Typography>
            {subtitle != null && (
                <Typography variant="body2" color="text.secondary">
                    {subtitle}
                </Typography>
            )}
        </Box>
        {controls != null && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                {controls}
            </Box>
        )}
    </Box>
);

export const UsageLoadError = () => {
    const t = useCommonTranslation();
    return <Alert severity="error">{t('Modules.Admin.Usage.loadFailed')}</Alert>;
};

export const UsageEmpty = () => {
    const t = useCommonTranslation();
    return (
        <Typography variant="body2" color="text.secondary">
            {t('Modules.Admin.Usage.noData')}
        </Typography>
    );
};

/**
 * Every usage page's body: one column, capped at the design canvas's 1360px frame and centred, so
 * a wide screen doesn't spread tables and charts across it.
 */
export const PageStack = ({ children }: { children: ReactNode }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            minWidth: 0,
            width: '100%',
            maxWidth: 1360,
            mx: 'auto',
        }}
    >
        {children}
    </Box>
);

/** Two panels side by side that stack on a narrow screen; the first takes the extra width. */
export const PanelRow = ({ children }: { children: [ReactNode, ReactNode] }) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: '999 1 560px', minWidth: 0 }}>{children[0]}</Box>
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>{children[1]}</Box>
    </Box>
);

/** "5 minutes ago", with the exact date and time on hover. */
export const TimeAgo = ({ date }: { date: string }) => {
    const { formatDateString, formatTimeAgo } = useDateUtils();
    return (
        <span title={formatDateString({ date, removeTime: false, timeFormat: 'HH:mm' })}>
            {formatTimeAgo(date)}
        </span>
    );
};

/**
 * Headline numbers as one strip: a row of cells divided by rules, not a card each. On a narrow
 * screen the cells wrap two to a row.
 */
export const SummaryStrip = ({ children }: { children: ReactNode }) => (
    <Card variant="outlined">
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                '& > *': {
                    borderRight: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    mr: '-1px',
                    mb: '-1px',
                },
            }}
        >
            {children}
        </Box>
    </Card>
);

export const SummaryCell = ({
    label,
    value,
    note,
}: {
    label: string;
    value: ReactNode;
    note?: ReactNode;
}) => (
    <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
        <Typography
            variant="overline"
            color="text.secondary"
            sx={{ lineHeight: 1.4, letterSpacing: '0.08em' }}
        >
            {label}
        </Typography>
        <Typography
            component="p"
            sx={{
                m: 0,
                fontSize: 26,
                fontWeight: 700,
                lineHeight: 1.1,
                fontVariantNumeric: 'tabular-nums',
            }}
        >
            {value}
        </Typography>
        {note != null && (
            <Typography variant="caption" color="text.secondary">
                {note}
            </Typography>
        )}
    </Box>
);

/** One card for a side column; its sections are divided by rules instead of being cards of their own. */
export const SidePanel = ({ children }: { children: ReactNode }) => (
    <Card variant="outlined" sx={{ minWidth: 0 }}>
        <Box sx={{ '& > section + section': { borderTop: 1, borderColor: 'divider' } }}>
            {children}
        </Box>
    </Card>
);

export const SideSection = ({
    title,
    note,
    warning = false,
    children,
}: {
    title: string;
    /** A short qualifier beside the title: "time in portal", "9 people · 2 organisations". */
    note?: ReactNode;
    /** Shaded as something to look into ("Going quiet"). */
    warning?: boolean;
    children: ReactNode;
}) => (
    <Box
        component="section"
        aria-label={title}
        sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            bgcolor: theme => (warning ? alpha(theme.palette.warning.main, 0.08) : undefined),
        }}
    >
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 1,
                flexWrap: 'wrap',
            }}
        >
            <Typography
                component="h2"
                variant="overline"
                color={warning ? 'warning.dark' : 'text.secondary'}
                sx={{ lineHeight: 1.4, letterSpacing: '0.08em' }}
            >
                {title}
            </Typography>
            {note != null && (
                <Typography variant="caption" color={warning ? 'warning.dark' : 'text.secondary'}>
                    {note}
                </Typography>
            )}
        </Box>
        {children}
    </Box>
);

/**
 * The end of a list that loads more when it scrolls into view (a little before, so it's usually
 * there already). The button is the same thing for anyone not scrolling: keyboard users, tests.
 */
export const LoadMore = ({
    loading,
    failed,
    onLoad,
    watchKey,
}: {
    loading: boolean;
    failed: boolean;
    onLoad: () => void;
    watchKey: number;
}) => {
    const t = useCommonTranslation();
    const ref = useRef<HTMLDivElement>(null);
    const load = useRef(onLoad);
    load.current = onLoad;

    useEffect(() => {
        const element = ref.current;
        if (!element || failed) return undefined;
        const observer = new IntersectionObserver(
            entries => {
                if (entries.some(entry => entry.isIntersecting)) load.current();
            },
            { rootMargin: '400px' },
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, [watchKey, failed]);

    return (
        <Box ref={ref} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: '64px', pb: 1 }}>
            {loading ? (
                <>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                        {t('Modules.Admin.Usage.loadingMore')}
                    </Typography>
                </>
            ) : (
                <>
                    {failed && (
                        <Typography variant="body2" color="error">
                            {t('Modules.Admin.Usage.loadMoreFailed')}
                        </Typography>
                    )}
                    <Button size="small" onClick={onLoad}>
                        {t(
                            failed
                                ? 'Modules.Admin.Usage.tryAgain'
                                : 'Modules.Admin.Usage.loadMore',
                        )}
                    </Button>
                </>
            )}
        </Box>
    );
};
